import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import React from 'react';

interface ReadOnlyFieldProps {
  label: React.ReactNode;
  value: string | React.ReactNode;
  variant?: 'default' | 'multi-line';
  icon?: React.ReactNode;
}

/**
 * A component that displays a label and value in a read-only format.
 * Used to show form field values when the form is not in edit mode.
 */
const ReadOnlyField = ({
  label,
  value,
  variant = 'default',
  icon,
}: ReadOnlyFieldProps) => {
  const theme = useTheme();

  const hasValue = value !== null && value !== undefined && value !== '';

  const displayValue = hasValue ? value : t('common.not_specified');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: theme.brandV2.colors.treadGray3,
            }}
          >
            {icon}
          </Box>
        )}
        <Typography
          variant="subtitle2"
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: theme.brandV2.colors.treadGray3,
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        variant="body1"
        sx={{
          fontSize: '14px',
          color: hasValue
            ? theme.brandV2.colors.treadBlack
            : theme.brandV2.colors.treadGray4,
          fontStyle: hasValue ? 'normal' : 'italic',
          whiteSpace: variant === 'multi-line' ? 'pre-wrap' : 'normal',
        }}
      >
        {displayValue}
      </Typography>
    </Box>
  );
};

export { ReadOnlyField };
